import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { AppDispatch } from "app/store";
import { useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition, Select } from "common/elements";
import I18n from "features/shared/i18n";
import { updateUser } from "features/player/api";

interface BirthdayEditModalProps {
  open: boolean;
  currentBirthday: string;
  uid: string;
  onClose: () => void;
}

const isValidBirthday = (date: string): boolean => {
  if (!date) return false;
  if (date === "0000-00-00" || date.startsWith("0000")) return false;
  const d = dayjs(date);
  if (!d.isValid()) return false;
  if (d.year() < 1920) return false;
  return true;
};

export const BirthdayEditModal = ({
  open,
  currentBirthday,
  uid,
  onClose,
}: BirthdayEditModalProps) => {
  const { scale } = useScale();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const [birthday, setBirthday] = useState<string>("");
  const [birthdayYear, setBirthdayYear] = useState<number | null>(null);
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(null);
  const [birthdayDay, setBirthdayDay] = useState<number | null>(null);
  const [disable, setDisable] = useState(false);

  // ドロップダウン開閉状態
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);

  // ボタンref
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  // 年の範囲
  const currentYear = dayjs().year();
  const minYear = currentYear - 100;
  const maxYear = currentYear;

  useEffect(() => {
    if (open) {
      if (isValidBirthday(currentBirthday)) {
        const d = dayjs(currentBirthday);
        setBirthday(currentBirthday);
        setBirthdayYear(d.year());
        setBirthdayMonth(d.month() + 1);
        setBirthdayDay(d.date());
      } else {
        setBirthday("");
        setBirthdayYear(null);
        setBirthdayMonth(null);
        setBirthdayDay(null);
      }
      setDisable(false);
    }
  }, [open, currentBirthday]);

  const selectedYear = birthdayYear;
  const selectedMonth = birthdayMonth;
  const selectedDay = birthdayDay;
  const isBirthdayComplete =
    selectedYear !== null && selectedMonth !== null && selectedDay !== null;

  // 年オプション
  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
    const year = maxYear - i;
    return { value: year, label: `${year}${I18n.t("MSG_BIRTHDAY_YEAR_SUFFIX_BY_PARENT")}` };
  });

  // 月オプション
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t("MSG_BIRTHDAY_MONTH_SUFFIX_BY_PARENT")}`,
  }));

  // 日オプション（年月に応じて動的に計算）
  const daysInMonth =
    selectedYear && selectedMonth ? dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth() : 31;
  const dayOptions = Array.from({ length: daysInMonth }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t("MSG_BIRTHDAY_DAY_SUFFIX_BY_PARENT")}`,
  }));

  const handleYearChange = (year: number) => {
    const month = selectedMonth;
    const day = selectedDay;
    let validDay = day;
    if (month && day) {
      const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
      validDay = Math.min(day, maxDay);
      setBirthdayDay(validDay);
    }
    setBirthdayYear(year);
    if (month && validDay) {
      setBirthday(dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"));
    } else {
      setBirthday("");
    }
  };

  const handleMonthChange = (month: number) => {
    const year = selectedYear;
    const day = selectedDay;
    let validDay = day;
    if (year && day) {
      const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
      validDay = Math.min(day, maxDay);
      setBirthdayDay(validDay);
    }
    setBirthdayMonth(month);
    if (year && validDay) {
      setBirthday(dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"));
    } else {
      setBirthday("");
    }
  };

  const handleDayChange = (day: number) => {
    const year = selectedYear;
    const month = selectedMonth;
    setBirthdayDay(day);
    if (year && month) {
      setBirthday(dayjs(`${year}-${month}-${day}`).format("YYYY-MM-DD"));
    } else {
      setBirthday("");
    }
  };

  const handleClick = () => {
    if (!disable) {
      setDisable(true);

      handleDialog({ type: DialogType.LOADING, value: true });

      dispatch(
        updateUser({
          uid,
          userInfo: { birthday: birthday || "" },
        }),
      )
        .unwrap()
        .then(() => {
          toast(
            <div>
              <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
            </div>,
          );
          onClose();
        })
        .catch((error) => {
          console.error(error);
          setDisable(false);
        })
        .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
    }
  };

  const buttonBase =
    "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black";
  const yearButtonClass = `${buttonBase} w-[200px]`;
  const monthDayButtonClass = `${buttonBase} w-[150px]`;

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[680px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-green-100">
                  calendar_month
                </span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_BIRTHDAY_EDIT_TITLE")}
                </h2>
              </div>
              <div className="flex-col-view items-center py-4">
                <div className="flex-row-el flex-center gap-4 my-6">
                  {/* 年 */}
                  <button
                    ref={yearRef}
                    onClick={() => setYearOpen(true)}
                    className={yearButtonClass}
                  >
                    <span>
                      {selectedYear
                        ? `${selectedYear}${I18n.t("MSG_BIRTHDAY_YEAR_SUFFIX_BY_PARENT")}`
                        : I18n.t("MSG_BIRTHDAY_YEAR_SUFFIX_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={yearRef}
                    open={yearOpen}
                    onClose={() => setYearOpen(false)}
                    value={selectedYear}
                    onChange={handleYearChange}
                    options={yearOptions}
                  />

                  {/* 月 */}
                  <button
                    ref={monthRef}
                    onClick={() => setMonthOpen(true)}
                    className={monthDayButtonClass}
                  >
                    <span>
                      {selectedMonth
                        ? `${selectedMonth}${I18n.t("MSG_BIRTHDAY_MONTH_SUFFIX_BY_PARENT")}`
                        : I18n.t("MSG_BIRTHDAY_MONTH_SUFFIX_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={monthRef}
                    open={monthOpen}
                    onClose={() => setMonthOpen(false)}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    options={monthOptions}
                  />

                  {/* 日 */}
                  <button
                    ref={dayRef}
                    onClick={() => setDayOpen(true)}
                    className={monthDayButtonClass}
                  >
                    <span>
                      {selectedDay
                        ? `${selectedDay}${I18n.t("MSG_BIRTHDAY_DAY_SUFFIX_BY_PARENT")}`
                        : I18n.t("MSG_BIRTHDAY_DAY_SUFFIX_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={dayRef}
                    open={dayOpen}
                    onClose={() => setDayOpen(false)}
                    value={selectedDay}
                    onChange={handleDayChange}
                    options={dayOptions}
                  />
                </div>
              </div>

              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleClick}
                  disabled={disable || !isBirthdayComplete}
                  className={`flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] transition-all ${
                    disable || !isBirthdayComplete
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:opacity-90"
                  }`}
                >
                  {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
